import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Paper, Stack, Typography, Tab, Tabs, LinearProgress } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import useNotification from '../../../../../core/hooks/use_notification';
import TextFieldFormik from '../../../../common/text_field';
import { SubmitButton } from '../../../../common/button';
import BottomActionBar from "../../../../common/bottom_action_bar";

import { getLanguagesState } from '../../../../../features/product/get_languages';
import { getProductTranslationsAction, getProductTranslationsState } from '../../../../../features/product/get_product_translations';
import { updateProductTranslationsAction, updateProductTranslationsState } from '../../../../../features/product/update_product_translations';
import { getProductTranslationsNotificationData, updateProductTranslationsNotificationData } from "../../../../../core/utils/notification_utils";
import { ApiRequestStatusEnum } from "../../../../../core/enums/common/api";

const ProductDetailsForm = ({ productId }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const getLanguages = useSelector(getLanguagesState);
    const getProductTranslations = useSelector(getProductTranslationsState);
    const updateProductTranslations = useSelector(updateProductTranslationsState);

    const [activeLangTab, setActiveLangTab] = useState(0);

    useEffect(() => {
        if (productId) {
            dispatch(getProductTranslationsAction({ productId }))
                .then(response => showErrorMessage(getProductTranslationsNotificationData(response)));
        }
    }, [dispatch, showErrorMessage, productId]);

    if (getProductTranslations?.loading || !getProductTranslations?.data || getLanguages?.loading) {
        return <LinearProgress />;
    }

    const availableLanguages = getLanguages?.data || [];
    const translationsData = getProductTranslations?.data?.translations || {};

    const initialValues = {
        translations: availableLanguages.reduce((acc, lang) => {
            const langData = translationsData[lang.languageCode] || {};
            acc[lang.languageCode] = {
                name: langData.name || '',
                description: langData.description || '',
                productComposition: langData.productComposition || '',
                preparation: langData.preparation || ''
            };
            return acc;
        }, {})
    };

    const schema = Yup.object().shape({
        translations: Yup.object().shape({
            PL: Yup.object().shape({
                name: Yup.string().required('Nazwa w języku polskim jest wymagana'),
                productComposition: Yup.string().required('Składniki w języku polskim są wymagane'),
            })
        })
    });

    const onSubmit = (values) => {
        const translationsToSend = {};

        Object.entries(values.translations).forEach(([langCode, data]) => {
            if (data.name || data.description || data.productComposition || data.preparation) {
                translationsToSend[langCode] = data;
            }
        });

        const payload = {
            productId,
            form: {
                translations: translationsToSend
            }
        };

        dispatch(updateProductTranslationsAction(payload))
            .then(response => {
                showNotification(updateProductTranslationsNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    dispatch(getProductTranslationsAction({ productId }))
                        .then(response => showErrorMessage(getProductTranslationsNotificationData(response)));
                }
            })
    };

    const currentLangCode = availableLanguages[activeLangTab]?.languageCode || 'PL';

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }
        >
            <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                <Box sx={ { flexGrow: 1, mb: 4 } }>
                    <Paper variant="outlined">

                        <Box sx={ { borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, bgcolor: 'slate.50' } }>
                            <Tabs value={ activeLangTab } onChange={ (_, val) => setActiveLangTab(val) } variant="scrollable">
                                { availableLanguages.map((lang, index) => (
                                    <Tab key={ lang.languageCode } label={ lang.languageName } />
                                )) }
                            </Tabs>
                        </Box>

                        <Box sx={ { p: 4 } }>
                            <Typography variant="subtitle2" color="textSecondary" sx={ {
                                mb: 3,
                                textTransform: 'uppercase',
                                fontWeight: 700,
                                letterSpacing: '0.5px'
                            } }>
                                Treści wielojęzyczne ({ currentLangCode })
                            </Typography>

                            <Stack spacing={ 3 } key={ currentLangCode }>
                                <TextFieldFormik
                                    name={ `translations.${currentLangCode}.name` }
                                    label="Nazwa"
                                    required={ currentLangCode === 'PL' }
                                />
                                <TextFieldFormik
                                    name={ `translations.${currentLangCode}.description` }
                                    label="Opis"
                                    multiline
                                    rows={ 2 }
                                />
                                <TextFieldFormik
                                    name={ `translations.${currentLangCode}.productComposition` }
                                    label="Składniki"
                                    multiline
                                    rows={ 3 }
                                    required={ currentLangCode === 'PL' }
                                />
                                <TextFieldFormik
                                    name={ `translations.${currentLangCode}.preparation` }
                                    label="Sposób przygotowania"
                                    multiline
                                    rows={ 3 }
                                />
                            </Stack>
                        </Box>

                    </Paper>
                </Box>

                <BottomActionBar>
                    <SubmitButton isLoading={ updateProductTranslations?.loading }>
                        Zapisz wszystkie tłumaczenia
                    </SubmitButton>
                </BottomActionBar>

            </Box>
        </Formik>
    );
};

export default ProductDetailsForm;